import React, { ReactNode } from "react";

import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { Button, Popconfirm } from "@mapmycustomers/ui";

import styles from "./BottomNavigationBar.module.scss";

interface Props {
  backButtonTitle?: string;
  cancelButtonTitle?: string;
  nextButtonConfirmationCancelText?: string;
  nextButtonConfirmationMessage?: ReactNode;
  nextButtonConfirmationMessageIcon?: ReactNode;
  nextButtonConfirmationOkText?: string;
  nextButtonErrorMessage?: string;
  nextButtonTitle?: string;
  nextDisabled?: boolean;
  onCancelClick?: () => void;
  onConfirmationCancel?: () => void;
  onConfirmationOk?: () => void;
  onNextClick: () => void;
  onPreviousClick: () => void;
  previousDisabled?: boolean;
  previousHidden?: boolean;
}

const BottomNavigationBar: React.FC<Props> = ({
  backButtonTitle,
  cancelButtonTitle,
  nextButtonConfirmationCancelText,
  nextButtonConfirmationMessage,
  nextButtonConfirmationMessageIcon,
  nextButtonConfirmationOkText,
  nextButtonErrorMessage,
  nextButtonTitle,
  nextDisabled,
  onCancelClick,
  onConfirmationCancel,
  onConfirmationOk,
  onNextClick,
  onPreviousClick,
  previousDisabled,
  previousHidden,
}) => {
  const intl = useIntl();
  const nextButtonTitleText =
    nextButtonTitle ||
    intl.formatMessage({
      id: "bottomNavigationBar.buttonBar.nextButtonTitle",
      defaultMessage: "Continue",
      description: "Text of next button",
    });
  const backButtonTitleText =
    backButtonTitle ||
    intl.formatMessage({
      id: "bottomNavigationBar.buttonBar.backButtonTitle",
      defaultMessage: "Go Back",
      description: "Text of previous button",
    });
  const cancelButtonTitleText =
    cancelButtonTitle ||
    intl.formatMessage({
      id: "bottomNavigationBar.buttonBar.cancelButtonTitle",
      defaultMessage: "Cancel",
      description: "Text of previous button",
    });

  return (
    <Row
      align="middle"
      className={styles.row}
      justify={cancelButtonTitle ? "space-between" : "end"}
    >
      {cancelButtonTitle ? <Button onClick={onCancelClick}>{cancelButtonTitleText}</Button> : null}

      <Row>
        {!previousHidden && (
          <Button disabled={previousDisabled} onClick={onPreviousClick} type="link">
            {backButtonTitleText}
          </Button>
        )}
        <Tooltip
          overlayClassName={styles.errorTooltip}
          title={nextDisabled ? nextButtonErrorMessage : null}
        >
          <Popconfirm
            cancelText={
              nextButtonConfirmationCancelText ??
              intl.formatMessage({
                id: "bottomNavigationBar.buttonBar.no",
                defaultMessage: "No",
                description: "No-confirmation text for bottom navigation",
              })
            }
            description={nextButtonConfirmationMessage}
            disabled={!nextButtonConfirmationMessage || nextDisabled}
            okText={
              nextButtonConfirmationOkText ??
              intl.formatMessage({
                id: "bottomNavigationBar.buttonBar.yes",
                defaultMessage: "Yes",
                description: "Yes-confirmation text for bottom navigation",
              })
            }
            onCancel={onConfirmationCancel}
            onConfirm={onConfirmationOk ?? onNextClick}
            placement="top"
            {...(nextButtonConfirmationMessageIcon
              ? { icon: nextButtonConfirmationMessageIcon }
              : {})}
          >
            <Button
              className={styles.nextButton}
              disabled={nextDisabled}
              onClick={nextButtonConfirmationMessage ? undefined : onNextClick}
              type="primary"
            >
              {nextButtonTitleText}
            </Button>
          </Popconfirm>
        </Tooltip>
      </Row>
    </Row>
  );
};

export default BottomNavigationBar;
